/**
 * Exchange Online Plan 2 (email-storage upgrade) billing resolution.
 *
 * The "Increase Email Storage" ticket type (`m365_increase_email_storage`) adds
 * an EOLP2 mailbox upgrade. This module is the single brain shared by:
 *   - the `emailStorageBuffered` pricing rule (the ticket's current-period line), and
 *   - TicketEntryPage's save-time schedule sync (the recurring schedule mutation).
 *
 * Both must agree on the billing term (monthly vs annual), the SKU, and the
 * target schedule, so the decision lives here rather than being duplicated.
 *
 * Rules:
 *   - Term follows the customer's existing M365 subscription cadence; if the
 *     customer bills M365 both monthly AND annually, monthly wins.
 *   - Monthly → charge the full month on the ticket, UNLESS we're within the
 *     last `bufferDays` calendar days of the month (default 2) — then the ticket
 *     charge is waived because the next recurring invoice already covers it.
 *   - Annual → pro-rate over the months remaining, to co-terminate with the
 *     customer's existing annual commitment.
 *   - No M365 subscription on any schedule → `blocked` (the ticket adds nothing
 *     for EOLP2; the tech is warned to set the subscription up first).
 * The recurring schedule increment/create ALWAYS happens when not blocked —
 * the month-end buffer only waives the one-off ticket charge.
 */
import type { RenewalScheduleItem } from '@techgitter/contracts';
import { getProductBySku } from '../hooks/useProductCatalog';
import { skuMatchesCategory } from '../data/licenseCategories';

/** EOLP2 SKUs by billing term (already in the product catalog). */
export const EOLP2_MONTHLY_SKU = 'BML.M365EOLP2.Monthly';
export const EOLP2_ANNUAL_SKU = 'BML.M365EOLP2.Annual';

/**
 * Calendar days at the end of the month within which a MONTHLY customer's
 * current-month EOLP2 charge is waived (the next recurring invoice covers it).
 * Configurable; default 2. If this ever needs to change without a deploy,
 * promote it to an admin App Setting and thread the value through the callers.
 */
export const EOLP2_MONTH_END_BUFFER_DAYS = 2;

export type Eolp2Term = 'monthly' | 'annual';

export interface Eolp2Billing {
  /** True when the customer has no (unpaused) M365 subscription on any schedule. */
  blocked: boolean;
  /** Billing cadence chosen from the customer's M365 schedule (both → monthly). */
  term?: Eolp2Term;
  /** EOLP2 SKU for the chosen term. */
  sku?: string;
  /** Per-unit charge for THIS ticket's invoice (0 inside the month-end buffer). */
  ticketUnitCost?: number;
  /** Ticket line quantity — 0 means "no charge this period" (buffer). */
  ticketQuantity?: number;
  /** Full recurring per-period rate for the SKU (what the schedule line bills). */
  recurringUnitCost?: number;
  /** Schedule the recurring EOLP2 line should be incremented/created on. */
  targetScheduleId?: string;
  /** Existing EOLP2 line on that schedule, if any (present → increment; absent → create). */
  existingLine?: RenewalScheduleItem;
  /** Human-readable pricing explanation for the ticket line item. */
  explanation: string;
  /** Optional blurb appended to the ticket line detail (e.g. proration note). */
  lineDetailAppend?: string;
}

/** True when `now` falls within the last `bufferDays` calendar days of its month. */
export function isMonthEndBuffer(now: Date, bufferDays: number): boolean {
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return now.getDate() > daysInMonth - bufferDays;
}

function monthsBetween(from: Date, to: Date): number {
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

export function resolveEolp2Billing(
  schedule: RenewalScheduleItem[],
  now: Date,
  bufferDays: number = EOLP2_MONTH_END_BUFFER_DAYS,
): Eolp2Billing {
  // M365-family subscription lines on the customer's (active) schedules, split by cadence.
  const m365 = schedule.filter(
    (i) => !i.paused && skuMatchesCategory(i.productSku, 'licensing-m365'),
  );
  const monthly = m365.filter((i) => i.frequency.toLowerCase() === 'monthly');
  const annual = m365.filter((i) => i.frequency.toLowerCase() === 'annually');

  if (monthly.length === 0 && annual.length === 0) {
    return { blocked: true, explanation: 'No M365 subscription schedule — EOLP2 not added' };
  }

  // Both cadences present → monthly wins.
  const term: Eolp2Term = monthly.length > 0 ? 'monthly' : 'annual';
  const cohort = term === 'monthly' ? monthly : annual;
  const sku = term === 'monthly' ? EOLP2_MONTHLY_SKU : EOLP2_ANNUAL_SKU;

  // Target = the schedule already hosting the customer's M365 lines for this
  // cadence; prefer one that already bills EOLP2 (so we increment, not duplicate).
  const existingLine = cohort.find((i) => i.productSku === sku);
  const targetScheduleId = (existingLine ?? cohort[0]).scheduleId;

  const product = getProductBySku(sku);
  const rate = product?.unitCost ?? 0;
  const displayName = product?.displayName ?? sku;

  const common = {
    blocked: false,
    term,
    sku,
    recurringUnitCost: rate,
    targetScheduleId,
    existingLine,
  } as const;

  if (term === 'monthly') {
    if (isMonthEndBuffer(now, bufferDays)) {
      return {
        ...common,
        ticketUnitCost: rate,
        ticketQuantity: 0,
        explanation: `${displayName} — within ${bufferDays}-day month-end buffer, no charge this month (next recurring invoice covers it)`,
      };
    }
    return {
      ...common,
      ticketUnitCost: rate,
      ticketQuantity: 1,
      explanation: `${displayName} — full month`,
    };
  }

  // Annual: pro-rate over months remaining to co-terminate with the renewal.
  const nextRun = new Date(cohort[0].nextRun);
  const monthsRemaining = monthsBetween(now, nextRun);
  const annualRate = sku.endsWith('.Annual') ? rate : rate * 12;

  if (monthsRemaining <= 0) {
    return {
      ...common,
      ticketUnitCost: annualRate,
      ticketQuantity: 1,
      explanation: `${displayName} — full annual rate ($${annualRate.toFixed(2)}/yr)`,
    };
  }

  const prorated = Math.round(((annualRate * monthsRemaining) / 12) * 100) / 100;
  const dueMonth = nextRun.toLocaleString('en-AU', { month: 'long' });
  return {
    ...common,
    ticketUnitCost: prorated,
    ticketQuantity: 1,
    lineDetailAppend: `pro-rated to co-incide with existing annual subscription due ${dueMonth}`,
    explanation: `${displayName} — prorated ${monthsRemaining}/12 months ($${annualRate.toFixed(2)}/yr)`,
  };
}
